import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { Locale } from "../../core/api/model";
import { useCurrentUser } from "../../core/hooks/user";
import SpinnerModal from "../../core/spinner-modal";
import { CorporationClassification } from "../../generated/platform";
import { CorporationProductList } from "../components/CorporationProductList";
import { useRequestMeeting } from "../hooks";
import { Box, Container } from "@material-ui/core";

interface Props {
  locale: Locale;
  showErrorModal: (title: string, description: string, raw?: any) => void;
}

export const Products = ({ locale, showErrorModal }: Props) => {
  const { corpId } = useParams<{ corpId: string }>();
  const { request } = useRequestMeeting();
  const intl = useIntl();
  const history = useHistory();

  const showError = useCallback(
    (raw?: any) => {
      showErrorModal(
        intl.formatMessage({
          id: "RequestMeetingError",
          defaultMessage: "Terminanfrage fehlgeschlagen",
        }),
        intl.formatMessage({
          id: "RequestMeetingInvalidArguments",
          defaultMessage: "Leider konnte der Termin nicht angefragt werden.",
        }),
        raw
      );
    },
    [intl, showErrorModal]
  );

  const { user, loading } = useCurrentUser(showError);

  const handleMeetingClick = useCallback(
    (classification: CorporationClassification) => {
      if (
        !user?.partId ||
        !classification.expert?.partId ||
        !classification.classificationId
      ) {
        showError();
        return;
      }

      const promiseOrFalse = request(
        classification.classificationId,
        user.partId,
        classification.expert.partId,
        process.env.REACT_APP_EXPO_CODE || "",
        Number.parseInt(corpId)
      );

      if (!promiseOrFalse) {
        showError();
        return;
      }
      promiseOrFalse
        .then((v) =>
          history.push(`/virtual-meet/profile/${v.data?.createMeeting?.uuid}`)
        )
        .catch((e) => showError(e));
    },
    [request, history, showError, user, corpId]
  );

  return (
    <Box
      component={"main"}
      className="main-background"
      pt={{ xs: 5, md: 5 }}
      pb={{ xs: 7.5, md: 10 }}
    >
      <Container maxWidth="lg">
        <SpinnerModal active={loading} />
        {user && (
          <CorporationProductList
            requestLabel={intl.formatMessage({
              id: "RequestMeeting",
              defaultMessage: "Zum Meeting",
            })}
            onRequestMeeting={handleMeetingClick}
            locale={locale}
            corpId={Number.parseInt(corpId)}
          />
        )}
      </Container>
    </Box>
  );
};
